import { ILayoutScalerOptions } from "../types/layout_scaler_options.js";

export interface ILayoutScalerCoordOptions extends ILayoutScalerOptions
{
  interpolate_coords?: boolean;
  base_coord_w?: number;
  base_coord_h?: number;

  design_w_min?: number;
  design_w_max?: number;
  design_h_min?: number;
  design_h_max?: number;
  design_h?: number;
}

export class LayoutScalerLocal
{
  private readonly DESIGN_W_MIN = 1080;
  private readonly DESIGN_W_MAX = 1440;
  private readonly DESIGN_H_MIN = 1920;
  private readonly DESIGN_H_MAX = 2340;

  private design_w_min: number;
  private design_w_max: number;
  private design_h_min: number;
  private design_h_max: number;

  private screen_w = 0;
  private screen_h = 0;

  private scale_s = 1;
  private visible_design_w = 0;
  private visible_design_h = 0;

  private interpolate_coords: boolean;
  private base_coord_w: number;
  private base_coord_h: number;

  constructor(
    screen_w: number,
    screen_h: number,
    opts: ILayoutScalerCoordOptions = {}
  )
  {
    this.design_w_min = opts.design_w_min ?? this.DESIGN_W_MIN;
    this.design_w_max = opts.design_w_max ?? this.DESIGN_W_MAX;

    this.design_h_min = opts.design_h_min ?? ( opts.design_h ?? this.DESIGN_H_MIN );
    this.design_h_max = opts.design_h_max ?? this.DESIGN_H_MAX;

    this.interpolate_coords = opts.interpolate_coords ?? false;
    this.base_coord_w = opts.base_coord_w ?? this.design_w_min;
    this.base_coord_h = opts.base_coord_h ?? this.design_h_min;

    this.updateScreenSize( screen_w, screen_h );
  }

  public static fromWindow( opts: ILayoutScalerCoordOptions = {} ): LayoutScalerLocal
  {
    return new LayoutScalerLocal( window.innerWidth, window.innerHeight, opts );
  }

  public updateScreenSize( screen_w: number, screen_h: number ): void
  {
    this.screen_w = Math.max( 1, Math.floor( screen_w ) );
    this.screen_h = Math.max( 1, Math.floor( screen_h ) );
    this.recompute();
  }

  private recompute(): void
  {
    const r = this.screen_w / this.screen_h;

    const ar_min = this.design_w_min / this.design_h_max;
    const ar_max = this.design_w_max / this.design_h_min;

    let vd_w = this.design_w_min;
    let vd_h = this.design_h_min;

    if ( r < ar_min )
    {
      vd_w = this.design_w_min;
      this.scale_s = this.screen_w / vd_w;

      const cand_h = this.screen_h / this.scale_s;
      vd_h = this.clamp( cand_h, this.design_h_min, this.design_h_max );
    }
    else if ( r > ar_max )
    {
      vd_h = this.design_h_min;
      this.scale_s = this.screen_h / vd_h;

      const cand_w = this.screen_w / this.scale_s;
      vd_w = this.clamp( cand_w, this.design_w_min, this.design_w_max );
    }
    else
    {
      let h = this.design_h_min;
      let w = r * h;

      if ( w < this.design_w_min )
      {
        h = this.design_w_min / r;
      }
      else if ( w > this.design_w_max )
      {
        h = this.design_w_max / r;
      }

      h = this.clamp( h, this.design_h_min, this.design_h_max );
      w = r * h;

      vd_w = this.clamp( w, this.design_w_min, this.design_w_max );
      vd_h = h;

      this.scale_s = this.screen_h / vd_h;
    }

    this.visible_design_w = vd_w;
    this.visible_design_h = vd_h;
  }

  public getScale(): number { return this.scale_s; }
  public getVisibleDesignWidth(): number { return this.visible_design_w; }
  public getVisibleDesignHeight(): number { return this.visible_design_h; }

  public widthFromDesign( design_px: number ): number
  {
    return Math.round( design_px * this.scale_s );
  }

  public heightFromDesign( design_px: number ): number
  {
    return Math.round( design_px * this.scale_s );
  }

  public cornerRadiusFromDesign( design_radius: number ): number
  {
    return Math.round( design_radius * this.scale_s );
  }

  private getWidthLerpT(): number
  {
    const w = this.clamp( this.visible_design_w, this.design_w_min, this.design_w_max );
    if ( this.design_w_max === this.design_w_min ) return 0;
    return ( w - this.design_w_min ) / ( this.design_w_max - this.design_w_min );
  }

  private getHeightLerpT(): number
  {
    const h = this.clamp( this.visible_design_h, this.design_h_min, this.design_h_max );
    if ( this.design_h_max === this.design_h_min ) return 0;
    return ( h - this.design_h_min ) / ( this.design_h_max - this.design_h_min );
  }

  private scaleCoordFromBaseW( design_value_at_base: number ): number
  {
    if ( !this.interpolate_coords ) return design_value_at_base;
    if ( this.base_coord_w === 0 ) return design_value_at_base;

    const k = this.visible_design_w / this.base_coord_w;
    return design_value_at_base * k;
  }

  private scaleCoordFromBaseH( design_value_at_base: number ): number
  {
    if ( !this.interpolate_coords ) return design_value_at_base;
    if ( this.base_coord_h === 0 ) return design_value_at_base;

    const k = this.visible_design_h / this.base_coord_h;
    return design_value_at_base * k;
  }

  public xFromDesign( design_x_at_base: number ): number
  {
    const design_x_adj = this.scaleCoordFromBaseW( design_x_at_base );
    return Math.round( design_x_adj * this.scale_s );
  }

  public yFromDesign( design_y_at_base: number ): number
  {
    const design_y_adj = this.scaleCoordFromBaseH( design_y_at_base );
    return Math.round( design_y_adj * this.scale_s );
  }

  public widthFromDesignAdaptive( design_w_at_base: number ): number
  {
    const w_adj = this.scaleCoordFromBaseW( design_w_at_base );
    return Math.round( w_adj * this.scale_s );
  }

  public heightFromDesignAdaptive( design_h_at_base: number ): number
  {
    const h_adj = this.scaleCoordFromBaseH( design_h_at_base );
    return Math.round( h_adj * this.scale_s );
  }

  private lerp( a: number, b: number, t: number ): number { return a + ( b - a ) * t; }

  public xFromLayouts( x_min: number, x_max: number ): number
  {
    const t = this.getWidthLerpT();
    const x_design = this.lerp( x_min, x_max, t );
    return Math.round( x_design * this.scale_s );
  }

  public yFromLayouts( y_min: number, y_max: number ): number
  {
    const t = this.getHeightLerpT();
    const y_design = this.lerp( y_min, y_max, t );
    return Math.round( y_design * this.scale_s );
  }

  public widthFromLayouts( w_min: number, w_max: number ): number
  {
    const t = this.getWidthLerpT();
    const w_design = this.lerp( w_min, w_max, t );
    return Math.round( w_design * this.scale_s );
  }

  public heightFromLayouts( h_min: number, h_max: number ): number
  {
    const t = this.getHeightLerpT();
    const h_design = this.lerp( h_min, h_max, t );
    return Math.round( h_design * this.scale_s );
  }

  // Like widthFromLayouts(), but lerps by the height factor instead of the
  // width factor — for elements whose width must track screen height (e.g.
  // scaling in lockstep with heightFromLayouts() on the same element).
  public widthFromHeightLayouts( w_min: number, w_max: number ): number
  {
    const t = this.getHeightLerpT();
    const w_design = this.lerp( w_min, w_max, t );
    return Math.round( w_design * this.scale_s );
  }

  public textSizeFromDesign( text_size: number ): number
  {
    return Math.round( text_size * this.scale_s );
  }

  public textBorderSizeFromDesign( border_size: number ): number
  {
    return Math.round( border_size * this.scale_s );
  }

  private clamp( v: number, a: number, b: number ): number
  {
    return Math.max( a, Math.min( b, v ) );
  }
}
